import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Gauge, Route, Fuel } from "lucide-react";
import { ProductLine } from "@/data/productLines";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface ProductCardCompactProps {
  product: ProductLine;
  index: number;
}

const ProductCardCompact = ({ product, index }: ProductCardCompactProps) => {
  // Get first variant specs for display
  const defaultVariant = product.variants[0];
  const specs = defaultVariant?.specs || [];
  
  // Extract key specs (Power, Speed, Range)
  const powerSpec = specs.find(s => s.label.toLowerCase().includes("power") || s.label.toLowerCase().includes("motor"));
  const speedSpec = specs.find(s => s.label.toLowerCase().includes("speed"));
  const rangeSpec = specs.find(s => s.label.toLowerCase().includes("range"));

  const isElectric = product.powerType === "electric";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="group"
    >
      <Link to={`/products/${product.id}`}>
        <div className="relative bg-card rounded-2xl border border-border/50 overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 h-full flex flex-col">
          {/* Image Container */}
          <div className="relative overflow-hidden bg-gradient-to-b from-muted/30 to-muted/10">
            <AspectRatio ratio={4 / 3}>
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
              />
            </AspectRatio>
            
            {/* Badges Overlay */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              <Badge 
                variant="secondary" 
                className="bg-background/90 backdrop-blur-sm text-xs font-medium"
              >
                {product.brand}
              </Badge>
              <Badge 
                variant={isElectric ? "default" : "outline"}
                className={isElectric 
                  ? "bg-eco-green/90 text-white text-xs" 
                  : "bg-amber-500/90 text-white border-amber-500 text-xs"
                }
              >
                {isElectric ? (
                  <><Zap className="w-3 h-3 mr-1" /> Electric</>
                ) : (
                  <><Fuel className="w-3 h-3 mr-1" /> Gas</>
                )}
              </Badge>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 flex flex-col flex-1">
            {/* Name and Tagline */}
            <h3 className="text-xl font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
              {product.name}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              {product.tagline}
            </p>

            {/* Key Specs Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {powerSpec && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted/50 rounded-full text-xs font-medium text-foreground">
                  <Zap className="w-3 h-3 text-primary" />
                  {powerSpec.value}
                </div>
              )}
              {speedSpec && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted/50 rounded-full text-xs font-medium text-foreground">
                  <Gauge className="w-3 h-3 text-primary" />
                  {speedSpec.value}
                </div>
              )}
              {rangeSpec && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted/50 rounded-full text-xs font-medium text-foreground">
                  <Route className="w-3 h-3 text-primary" />
                  {rangeSpec.value}
                </div>
              )}
            </div>

            {/* Variants indicator */}
            {product.variants.length > 1 && (
              <p className="text-xs text-muted-foreground mb-4">
                {product.variants.length} variants available
              </p>
            )}

            {/* Spacer to push CTA to bottom */}
            <div className="flex-1" />

            {/* CTA */}
            <Button 
              variant="ghost" 
              className="w-full justify-between group-hover:bg-primary/5 group-hover:text-primary mt-auto"
            >
              View Details
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCardCompact;
